/**
 * useSetupProgress (BLU-1376, Phase 95): the ONE state hook the rebuilt
 * onboarding page consumes.
 *
 * Sources, one each (CONTEXT review refinements):
 *  - `setup_required` / `setup_completed_at` from the org context;
 *  - `printMode`, `stepFlags` (Mark as done / Skip, D13), `ripSteps` and the
 *    live signals from `productSync.getOnboardingStatus`;
 *  - `isPureClient` / surfaces loading from `useOrgRoleSurfaces`.
 * Flow, visible list, completion, waiting, unlock walk and the latch all come
 * from ONE `selectSetupProgress` call (the sidebar uses the same function), so
 * this hook never re-derives the flow or the latch. It reads no browser
 * storage: the old per-browser flags are neither read nor imported (Q5).
 *
 * Writes patch the cache instead of refetching: `setStepState` cancels the
 * in-flight status query and `setData`s the returned `{ stepId, state }` into
 * `stepFlags`. The next poll or focus refetch reconciles with the server.
 *
 * Latch (D7): when every visible step is complete on a setup-required,
 * unfinished org, `finishSetup` is called once per org (a ref guard, cleared
 * on error; the call retries twice, then re-arms on the next window focus). Its returned `setupCompletedAt`
 * shows the finish state at once; the org context is then reloaded. The
 * status is never refetched for the latch.
 *
 * Poll: every 10 s only while the flow is client or owner, not latched and not
 * all complete (none on the question card). `allComplete` ignores the optional
 * Invite your team step (shown only with `users.manage`), so it never keeps the
 * poll running or holds up the latch.
 *
 * Auto-advance happens only after the user's own action on the selected step:
 * `setStepState(selected, 'done')` or `armAdvance(selected)` (the page calls it
 * from a step body's own save callback). Once the step reads complete the hook
 * holds `SETUP_SAVED_HOLD_MS` (0 under reduced motion), then selects the next
 * incomplete visible step, marks it `glowStep` and focuses its registered
 * heading with `preventScroll`. A background poll, a focus refetch or a
 * teammate's change (nothing armed) never moves the selection.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SetupStepState } from '@threadheads/shared';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { prefersReducedMotion } from '@/lib/scroll-container';
import {
  ALL_STEP_IDS,
  decisionsFromFlags,
  selectSetupProgress,
  type OnboardingStatus,
  type SetupFlow,
  type SetupProgress,
  type StepId,
} from '../lib/steps';
import { SETUP_SAVED_HOLD_MS, deriveVisualState, type StepVisualState } from '../lib/step-state';

const POLL_MS = 10_000;

/** A step's flag as the server stores it; `null` clears it. */
export type StepFlagState = SetupStepState | null;

export interface SetupProgressState {
  status: OnboardingStatus | undefined;
  isLoading: boolean;
  /** The status could not be loaded and there is no data to show. */
  isError: boolean;
  /** A background refetch failed; the data shown is the last good answer. */
  staleError: boolean;
  errorMessage: string | null;
  /** A manual `refresh()` is in flight (never a background poll). */
  refreshing: boolean;
  refresh: () => Promise<void>;

  flow: SetupFlow;
  printMode: OnboardingStatus['printMode'] | null;
  setupRequired: boolean;
  latched: boolean;
  /** The caller may save answers and flags (`org_settings.read`, same gate as the server). */
  canWrite: boolean;

  stepIds: readonly StepId[];
  completion: Record<StepId, boolean>;
  skipped: Record<StepId, boolean>;
  waiting: Record<StepId, boolean>;
  unlocked: Record<StepId, boolean>;
  visual: Record<StepId, StepVisualState>;
  /** Server Mark as done flags. */
  manualDone: Partial<Record<StepId, boolean>>;
  doneCount: number;
  total: number;
  showMeter: boolean;
  allComplete: boolean;
  currentStep: StepId | null;

  selected: StepId | null;
  select: (id: StepId) => void;
  /** Arm the auto-advance for `id` (called from a step body's own save callback). */
  armAdvance: (id: StepId) => void;
  /** The step the last auto-advance landed on; cleared when the selection changes again. */
  glowStep: StepId | null;
  /** Ref callback for a step's heading, so an auto-advance can focus it. */
  registerHeading: (id: StepId) => (el: HTMLElement | null) => void;
  setStepState: (id: StepId, state: StepFlagState) => void;
  /** The step whose flag write is in flight. */
  pendingStep: StepId | null;
}

function patchFlags(
  flags: OnboardingStatus['stepFlags'] | undefined,
  stepId: StepId,
  state: StepFlagState,
): OnboardingStatus['stepFlags'] {
  const next = { ...(flags ?? {}) };
  if (state === null) delete next[stepId];
  else next[stepId] = state;
  return next;
}

function clearTimer(ref: { current: ReturnType<typeof setTimeout> | null }): void {
  if (ref.current !== null) {
    clearTimeout(ref.current);
    ref.current = null;
  }
}

function pollInterval(progress: Pick<SetupProgress, 'flow' | 'latched' | 'allComplete'>): number | false {
  if (progress.flow !== 'client' && progress.flow !== 'owner') return false;
  return progress.latched || progress.allComplete ? false : POLL_MS;
}

export function useSetupProgress(): SetupProgressState {
  const { t } = useTranslation('onboarding');
  const { organization, reloadOrganization } = useOrganization();
  const { can } = useRole();
  const { isPureClient, isLoading: surfacesLoading } = useOrgRoleSurfaces();
  const utils = trpc.useUtils();

  const canWrite = can('org_settings.read');
  // The optional Invite your team step: the invite procedures' own gate
  // (adminProcedure = users.manage). It never changes the counts or the latch.
  const canInvite = can('users.manage');
  const orgId = organization?.id ?? null;
  const setupRequired = organization?.setup_required === true;

  // finishSetup's returned timestamp, keyed by org, so the finish state shows
  // before the org context reload lands (and never leaks across an org switch).
  const [localLatch, setLocalLatch] = useState<{ orgId: string; at: string } | null>(null);
  const localLatchedAt = localLatch && localLatch.orgId === orgId ? localLatch.at : null;
  const setupCompletedAt = organization?.setup_completed_at ?? localLatchedAt;

  // Focus refetch is the QueryClient default.
  const query = trpc.productSync.getOnboardingStatus.useQuery(undefined, {
    enabled: canWrite,
    refetchInterval: (q) =>
      pollInterval(
        selectSetupProgress({
          status: q.state.data,
          setupRequired,
          setupCompletedAt,
          isPureClient,
          surfacesLoading,
          canInvite,
        }),
      ),
  });
  const status = query.data;

  const progress = useMemo(
    () => selectSetupProgress({ status, setupRequired, setupCompletedAt, isPureClient, surfacesLoading, canInvite }),
    [status, setupRequired, setupCompletedAt, isPureClient, surfacesLoading, canInvite],
  );
  const { flow, stepIds, completion, waiting, unlocked, currentStep, latched, allComplete } = progress;

  const manualDone = useMemo(() => decisionsFromFlags(status?.stepFlags).done, [status?.stepFlags]);

  const visual = useMemo(() => {
    const visible = new Set(stepIds);
    return Object.fromEntries(
      ALL_STEP_IDS.map((id) => [
        id,
        visible.has(id)
          ? deriveVisualState({
              complete: completion[id],
              skipped: progress.skipped[id],
              waiting: waiting[id],
              unlocked: unlocked[id],
              isCurrent: id === currentStep,
            })
          : 'locked',
      ]),
    ) as Record<StepId, StepVisualState>;
  }, [stepIds, completion, progress.skipped, waiting, unlocked, currentStep]);

  // ---- Manual refresh ------------------------------------------------------
  const [refreshing, setRefreshing] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const refetchStatus = query.refetch;
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchStatus();
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, [refetchStatus]);

  // ---- Selection -----------------------------------------------------------
  // Pinned per (org, flow): a change of either resets to the current step.
  const selectionKey = `${orgId ?? ''}:${flow}`;
  const [pinned, setPinned] = useState<{ key: string; id: StepId } | null>(null);
  const selected: StepId | null =
    pinned && pinned.key === selectionKey && stepIds.includes(pinned.id) ? pinned.id : currentStep;
  const [glowStep, setGlowStep] = useState<StepId | null>(null);

  // Pin the default so a step completing in the background never moves it.
  useEffect(() => {
    if (selected && (!pinned || pinned.key !== selectionKey || pinned.id !== selected)) {
      setPinned({ key: selectionKey, id: selected });
    }
  }, [selected, selectionKey, pinned]);

  const armedRef = useRef<StepId | null>(null);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFocusRef = useRef<StepId | null>(null);
  const headingsRef = useRef(new Map<StepId, HTMLElement>());
  const headingCallbacksRef = useRef(new Map<StepId, (el: HTMLElement | null) => void>());
  // Whether the selected step has read incomplete since it was selected: an
  // armed step advances only once it turned complete after that.
  const seenIncompleteRef = useRef<{ id: StepId | null; seen: boolean }>({ id: null, seen: false });

  // Latest values for timers and callbacks (updated after every render).
  const latestRef = useRef({ selected, progress, selectionKey });
  useEffect(() => {
    latestRef.current = { selected, progress, selectionKey };
  });

  useEffect(() => () => clearTimer(advanceTimerRef), []);

  const tryAdvance = useCallback(() => {
    const { selected: from, progress: p } = latestRef.current;
    if (!from || armedRef.current !== from) return;
    if (!p.completion[from] || !seenIncompleteRef.current.seen || seenIncompleteRef.current.id !== from) return;
    armedRef.current = null;
    const reduced = prefersReducedMotion();
    clearTimer(advanceTimerRef);
    advanceTimerRef.current = setTimeout(
      () => {
        advanceTimerRef.current = null;
        const latest = latestRef.current;
        if (latest.selected !== from) return;
        const lp = latest.progress;
        const after = lp.stepIds.slice(lp.stepIds.indexOf(from) + 1);
        const next = after.find((id) => !lp.completion[id] && !lp.waiting[id]);
        if (!next) return;
        pendingFocusRef.current = next;
        setPinned({ key: latest.selectionKey, id: next });
        // The glow stays until the selection next changes: clearing it sooner
        // swaps the pane's animation class mid-glow and replays its fade-in.
        setGlowStep(reduced ? null : next);
      },
      reduced ? 0 : SETUP_SAVED_HOLD_MS,
    );
  }, []);

  const selectedComplete = selected ? completion[selected] : false;
  useEffect(() => {
    const seen = seenIncompleteRef.current;
    if (seen.id !== selected) {
      seenIncompleteRef.current = { id: selected, seen: !selectedComplete };
      return;
    }
    if (!selectedComplete) {
      seen.seen = true;
      return;
    }
    tryAdvance();
  }, [selected, selectedComplete, tryAdvance]);

  // Focus the landed step's heading once it is registered.
  useEffect(() => {
    if (!selected || pendingFocusRef.current !== selected) return;
    const el = headingsRef.current.get(selected);
    if (el) {
      pendingFocusRef.current = null;
      el.focus({ preventScroll: true });
    }
  }, [selected]);

  const registerHeading = useCallback((id: StepId) => {
    let cb = headingCallbacksRef.current.get(id);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        if (el) {
          headingsRef.current.set(id, el);
          if (pendingFocusRef.current === id && latestRef.current.selected === id) {
            pendingFocusRef.current = null;
            el.focus({ preventScroll: true });
          }
        } else if (headingsRef.current.get(id)) {
          headingsRef.current.delete(id);
        }
      };
      headingCallbacksRef.current.set(id, cb);
    }
    return cb;
  }, []);

  const select = useCallback((id: StepId) => {
    armedRef.current = null;
    pendingFocusRef.current = null;
    clearTimer(advanceTimerRef);
    setGlowStep(null);
    setPinned({ key: latestRef.current.selectionKey, id });
  }, []);

  const armAdvance = useCallback(
    (id: StepId) => {
      if (latestRef.current.selected !== id) return;
      armedRef.current = id;
      // The step body may already have refetched before calling back.
      tryAdvance();
    },
    [tryAdvance],
  );

  // ---- Step flags (Mark as done / Skip) ------------------------------------
  const [pendingStep, setPendingStep] = useState<StepId | null>(null);
  const stepStateMutation = trpc.onboarding.setStepState.useMutation({
    retry: false,
    onSuccess: async (result) => {
      await utils.productSync.getOnboardingStatus.cancel();
      utils.productSync.getOnboardingStatus.setData(undefined, (prev) =>
        prev ? { ...prev, stepFlags: patchFlags(prev.stepFlags, result.stepId, result.state) } : prev,
      );
    },
    onError: (_error, variables) => {
      if (armedRef.current === variables.stepId) armedRef.current = null;
      toast.error(t('stepFlag.saveError'));
    },
  });
  const mutateStepState = stepStateMutation.mutate;

  const setStepState = useCallback(
    (id: StepId, state: StepFlagState) => {
      if (state === 'done' && latestRef.current.selected === id) armedRef.current = id;
      setPendingStep(id);
      mutateStepState(
        { stepId: id, state },
        { onSettled: () => setPendingStep((current) => (current === id ? null : current)) },
      );
    },
    [mutateStepState],
  );

  // ---- Finish latch (D7) ---------------------------------------------------
  const latchGuardRef = useRef<string | null>(null);
  const latchCallOrgRef = useRef<string | null>(null);
  const finishMutation = trpc.onboarding.finishSetup.useMutation({
    // The call is idempotent (the server stamps the time once), so a blip
    // retries quietly; a still-failed call re-arms on the next window focus.
    retry: 2,
    onSuccess: (result) => {
      const forOrg = latchCallOrgRef.current;
      if (forOrg && result.setupCompletedAt) setLocalLatch({ orgId: forOrg, at: result.setupCompletedAt });
      // Refetches getCurrent itself (`fetch` with the default staleTime 0).
      reloadOrganization();
    },
    onError: () => {
      latchGuardRef.current = null;
    },
  });
  const mutateFinish = finishMutation.mutate;

  // Bumped on window focus, so a failed finish (guard cleared on error) tries
  // again in the same view instead of waiting for the next real change.
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    const onFocus = () => {
      if (latchGuardRef.current === null) setFocusTick((n) => n + 1);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  useEffect(() => {
    if (!orgId || !canWrite || !setupRequired || latched || !allComplete) return;
    if (latchGuardRef.current === orgId) return;
    latchGuardRef.current = orgId;
    latchCallOrgRef.current = orgId;
    mutateFinish({});
  }, [orgId, allComplete, latched, canWrite, setupRequired, mutateFinish, focusTick]);

  return {
    status,
    isLoading: query.isLoading,
    isError: query.isError && status === undefined,
    staleError: query.isError && status !== undefined,
    errorMessage: query.error?.message ?? null,
    refreshing,
    refresh,
    flow,
    printMode: status?.printMode ?? null,
    setupRequired,
    latched,
    canWrite,
    stepIds,
    completion,
    skipped: progress.skipped,
    waiting,
    unlocked,
    visual,
    manualDone,
    doneCount: progress.doneCount,
    total: progress.total,
    showMeter: progress.showMeter,
    allComplete,
    currentStep,
    selected,
    select,
    armAdvance,
    glowStep,
    registerHeading,
    setStepState,
    pendingStep,
  };
}
